import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';

import type {
  ProactivityExperimentCohortPreview,
  ProactivityExperimentDetail,
  ProactivityExperimentOptions,
  ProactivityExperimentSummary,
  CreateProactivityExperimentInput,
  UpdateProactivityExperimentCohortInput,
} from './proactivity-experiments-types';

const QUERY_KEY = ['proactivity-experiments'] as const;

export type ApiErrorBody = {
  detail?:
    | string
    | { user_error_message?: string }
    | { loc?: unknown[]; msg?: string }[];
};

export const apiErrorMessage = (
  body: ApiErrorBody | null,
  fallback: string
): string => {
  const detail = body?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && !Array.isArray(detail) && 'user_error_message' in detail) {
    return detail.user_error_message ?? fallback;
  }
  const issue = Array.isArray(detail) ? detail[0] : undefined;
  if (!issue) return fallback;
  const path =
    issue.loc
      ?.filter((segment) => segment !== 'body')
      .map(String)
      .join('.') || 'request';
  return `${path}: ${issue.msg ?? 'Invalid value'}`;
};

const useExperimentApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  return async <Response>(
    path: string,
    fallbackError: string,
    init?: RequestInit
  ): Promise<Response> => {
    const token = await getAccessTokenSilently();
    const response = await apiFetch(
      `${API_URL}/admin/proactivity-experiments${path}`,
      {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body && { 'Content-Type': 'application/json' }),
        },
      }
    );
    if (!response.ok) {
      const body = (await response
        .json()
        .catch(() => null)) as ApiErrorBody | null;
      throw new Error(apiErrorMessage(body, fallbackError));
    }
    if (response.status === 204) return undefined as Response;
    return response.json() as Promise<Response>;
  };
};

export const useProactivityExperimentOptions = () => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: [...QUERY_KEY, 'options'],
    queryFn: () =>
      request<ProactivityExperimentOptions>(
        '/options',
        'Failed to load experiment options'
      ),
  });
};

export const useProactivityExperiments = () => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      request<ProactivityExperimentSummary[]>(
        '',
        'Failed to load proactivity experiments'
      ),
  });
};

export const useProactivityExperiment = (
  experimentId: string,
  enabled: boolean
) => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: [...QUERY_KEY, experimentId],
    queryFn: () =>
      request<ProactivityExperimentDetail>(
        `/${experimentId}`,
        'Failed to load experiment recipients'
      ),
    enabled,
  });
};

export const useCreateProactivityExperiment = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProactivityExperimentInput) =>
      request<ProactivityExperimentSummary>(
        '',
        'Failed to create proactivity experiment',
        { method: 'POST', body: JSON.stringify(input) }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: true }),
  });
};

export const useUpdateProactivityExperimentCohort = (experimentId: string) => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProactivityExperimentCohortInput) =>
      request<ProactivityExperimentDetail>(
        `/${experimentId}/cohort`,
        'Failed to update the experiment cohort',
        { method: 'PATCH', body: JSON.stringify(input) }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

// `body` is the serialized cohort input, so a debounced string can drive the query key directly
export const useProactivityExperimentCohortPreview = (
  experimentId: string,
  body: string,
  enabled: boolean
) => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: [...QUERY_KEY, experimentId, 'cohort-preview', body],
    queryFn: () =>
      request<ProactivityExperimentCohortPreview>(
        `/${experimentId}/cohort/preview`,
        'Failed to preview the cohort change',
        { method: 'POST', body }
      ),
    enabled,
  });
};

export const useSyncProactivityExperimentCohort = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (experimentId: string) =>
      request<ProactivityExperimentDetail>(
        `/${experimentId}/cohort/sync`,
        'Failed to sync the experiment cohort',
        { method: 'POST' }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useSetProactivityExperimentPaused = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      experimentId,
      paused,
    }: {
      experimentId: string;
      paused: boolean;
    }) =>
      request<ProactivityExperimentDetail>(
        `/${experimentId}/${paused ? 'pause' : 'resume'}`,
        paused
          ? 'Failed to pause the experiment'
          : 'Failed to resume the experiment',
        { method: 'POST' }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useRevokeProactivityExperiment = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (experimentId: string) =>
      request<ProactivityExperimentDetail>(
        `/${experimentId}/revoke`,
        'Failed to revoke proactivity experiment',
        { method: 'POST' }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteProactivityExperiment = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (experimentId: string) =>
      request<void>(
        `/${experimentId}`,
        'Failed to delete proactivity experiment',
        { method: 'DELETE' }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
